/**
 * MEDIA-EDITOR-PROPERTY-SURFACES-A1 — Property media queue model + prepare orchestration.
 */

import { canLocalPreview, isHeicLike } from "../imageFileContract";
import { PREPARE_STATES } from "../mediaPrepare/constants";
import {
  createPrepareSession,
  mapPrepareErrorMessage,
  prepareImageForEditor,
} from "../mediaPrepare/prepareImageForEditor";

export const QUEUE_PHASE = Object.freeze({
  queued: "queued",
  checking: "checking",
  server_preview: "server_preview",
  ready: "ready",
  prepare_failed: "prepare_failed",
  uploading: "uploading",
  upload_failed: "upload_failed",
});

export const MEDIA_KIND_PHOTO = "image";
export const MEDIA_KIND_PLAN = "plan";
export const MEDIA_KIND_MESSAGE = "message_image";

export const MAX_USER_RETRY_ATTEMPTS = 2;

const RETRYABLE_PREPARE_CODES = new Set([
  "throttled",
  "decoder_unavailable",
  "processing_failed",
]);

export function surfaceForKind(kind) {
  if (kind === MEDIA_KIND_PLAN) return "property_plan";
  if (kind === MEDIA_KIND_MESSAGE) return "message_image";
  return "property_photo";
}

export function preparePhaseFromState(prepareState) {
  if (!prepareState) return QUEUE_PHASE.queued;
  switch (prepareState) {
    case PREPARE_STATES.selected:
    case PREPARE_STATES.native_check_pending:
      return QUEUE_PHASE.checking;
    case PREPARE_STATES.server_preview_pending:
      return QUEUE_PHASE.server_preview;
    case PREPARE_STATES.native_ready:
    case PREPARE_STATES.server_preview_ready:
      return QUEUE_PHASE.ready;
    case PREPARE_STATES.failed:
      return QUEUE_PHASE.prepare_failed;
    default:
      return QUEUE_PHASE.queued;
  }
}

export function queuePhaseLabel(phase) {
  const labels = {
    [QUEUE_PHASE.queued]: "У черзі",
    [QUEUE_PHASE.checking]: "Підготовка…",
    [QUEUE_PHASE.server_preview]: "Серверний перегляд…",
    [QUEUE_PHASE.ready]: "Готово",
    [QUEUE_PHASE.prepare_failed]: "Помилка підготовки",
    [QUEUE_PHASE.uploading]: "Завантаження…",
    [QUEUE_PHASE.upload_failed]: "Помилка завантаження",
  };
  return labels[phase] || phase;
}

export function createQueueItem(file, localId, kind, options = {}) {
  const previewable = canLocalPreview(file);
  const heicLike = isHeicLike(file);
  const previewUrl = previewable ? URL.createObjectURL(file) : null;
  const phase = heicLike ? QUEUE_PHASE.queued : QUEUE_PHASE.ready;

  return {
    localId,
    kind,
    file,
    uploadFile: heicLike ? null : file,
    previewUrl,
    previewRevoked: false,
    heicLike,
    phase,
    prepareState: heicLike ? null : PREPARE_STATES.native_ready,
    prepareError: null,
    uploadError: null,
    retryCount: 0,
    runId: null,
    session: null,
    edited: false,
    chatId: options.chatId != null ? options.chatId : null,
  };
}

export function revokeQueueItemPreview(item, registry = null) {
  return revokePreviewUrl(item?.previewUrl, registry || null, item);
}

export function createPreviewRevokeRegistry() {
  return new Set();
}

/**
 * Revoke a preview URL at most once via optional registry.
 * Does not mutate prior React state rows when a registry is provided.
 * Without a registry, legacy callers may still set previewRevoked — prefer
 * the queue owner registry path so prior state objects stay immutable.
 */
export function revokePreviewUrl(url, registry = null, legacyItem = null) {
  if (!url) return false;
  if (registry) {
    if (registry.has(url)) return false;
    URL.revokeObjectURL(url);
    registry.add(url);
    return true;
  }
  if (legacyItem?.previewRevoked) return false;
  URL.revokeObjectURL(url);
  if (legacyItem) {
    legacyItem.previewRevoked = true;
  }
  return true;
}

export function takeItemsByLocalIds(items, localIds) {
  const idSet = new Set(localIds);
  return items.filter((it) => idSet.has(it.localId));
}

/** Pure upload outcome transform — caller revokes succeeded previews outside updaters. */
export function applyUploadOutcomeToItems(items, succeededIds, failures = []) {
  const idSet = new Set(succeededIds);
  const byFailure = new Map(failures.map((f) => [f.localId, f.error]));
  return items
    .filter((it) => !idSet.has(it.localId))
    .map((it) => {
      if (!byFailure.has(it.localId)) return it;
      return {
        ...it,
        phase: QUEUE_PHASE.upload_failed,
        uploadError: byFailure.get(it.localId),
      };
    });
}

export function createPreviewUrlForFile(file) {
  return canLocalPreview(file) ? URL.createObjectURL(file) : null;
}

export function canLocalPreviewFile(file) {
  return canLocalPreview(file);
}

/**
 * Abort an app-owned prepare session at most once.
 * Returns true only when a physical abort() call was issued.
 */
export function abortQueueItemSession(item) {
  return abortPrepareSessionOnce(item?.session);
}

export function abortPrepareSessionOnce(session) {
  if (!session) return false;
  if (session.signal?.aborted) return false;
  try {
    session.abort();
    return true;
  } catch (_err) {
    return false;
  }
}

export function isItemUploadReady(item) {
  return item.phase === QUEUE_PHASE.ready && Boolean(item.uploadFile);
}

export function isItemPreparing(item) {
  return (
    item.phase === QUEUE_PHASE.queued
    || item.phase === QUEUE_PHASE.checking
    || item.phase === QUEUE_PHASE.server_preview
  );
}

export function hasUploadableQueueItems(items) {
  return items.some(
    (it) =>
      isItemUploadReady(it) || it.phase === QUEUE_PHASE.upload_failed,
  );
}

export function hasBlockingPrepare(items) {
  return items.some(isItemPreparing);
}

export function applyItemPatch(items, localId, patch) {
  return items.map((it) =>
    it.localId === localId ? { ...it, ...patch } : it,
  );
}

/**
 * Filter-only removal. Caller owns abort/revoke exactly once.
 */
export function removeQueueItem(items, localId) {
  return items.filter((it) => it.localId !== localId);
}

/**
 * Owner-level cleanup for one queue item: abort + revoke + drop row.
 * Returns next items array. Abort and revoke each run at most once here.
 */
export function cleanupAndRemoveQueueItem(items, localId, registry = null) {
  const target = items.find((it) => it.localId === localId);
  if (target) {
    abortQueueItemSession(target);
    revokeQueueItemPreview(target, registry);
  }
  return removeQueueItem(items, localId);
}

/** @deprecated Prefer buildReplacedPreviewItem + createPreviewUrlForFile outside updaters. */
export function replaceItemPreview(item, file) {
  const previewUrl = createPreviewUrlForFile(file);
  return buildReplacedPreviewItem(item, file, previewUrl);
}

/** Pure replacement row — caller creates previewUrl and revokes the old URL. */
export function buildReplacedPreviewItem(item, file, previewUrl) {
  return {
    ...item,
    uploadFile: file,
    previewUrl,
    previewRevoked: false,
    edited: true,
    phase: QUEUE_PHASE.ready,
    prepareState: PREPARE_STATES.native_ready,
    prepareError: null,
    uploadError: null,
  };
}

export function canRetryPrepare(item) {
  if (item.phase !== QUEUE_PHASE.prepare_failed) return false;
  if (!item.heicLike) return false;
  const code = item.prepareCode;
  if (code && !RETRYABLE_PREPARE_CODES.has(code)) return false;
  return item.retryCount < MAX_USER_RETRY_ATTEMPTS;
}

export function canRetryUpload(item) {
  return item.phase === QUEUE_PHASE.upload_failed && Boolean(item.uploadFile);
}

export function resetItemForUploadRetry(item) {
  if (!canRetryUpload(item)) return item;
  return {
    ...item,
    phase: QUEUE_PHASE.ready,
    uploadError: null,
  };
}

export function normalizeUploadError(entry) {
  if (typeof entry === "string" && entry.trim()) return entry.trim();
  if (entry && typeof entry.detail === "string" && entry.detail.trim()) {
    return entry.detail.trim();
  }
  if (entry && typeof entry.message === "string" && entry.message.trim()) {
    return entry.message.trim();
  }
  if (entry && typeof entry.error === "string" && entry.error.trim()) {
    return entry.error.trim();
  }
  return "Не вдалося завантажити файл.";
}

export function requireUploadFile(item) {
  if (!item?.uploadFile) {
    throw new Error("Файл ще не готовий до завантаження.");
  }
  return item.uploadFile;
}

export function uploadReadyItems(items) {
  return items.filter(isItemUploadReady);
}

export function itemsByKind(items, kind) {
  return items.filter((it) => it.kind === kind);
}

/**
 * Per-screen prepare lock: concurrency=1 with cancellation-aware waiting.
 */
export function createPropertyPrepareLock() {
  let inFlight = false;
  let waiters = [];
  let generation = 0;

  const rejectAllWaiters = () => {
    generation += 1;
    const pending = waiters;
    waiters = [];
    pending.forEach((w) => {
      w.reject({ cancelled: true });
    });
  };

  const wakeNext = () => {
    while (waiters.length > 0) {
      const next = waiters.shift();
      if (next.gen !== generation) {
        next.reject({ cancelled: true });
        continue;
      }
      next.resolve();
      return;
    }
  };

  return {
    cancelAllWaiters: rejectAllWaiters,
    bumpGeneration: rejectAllWaiters,
    async runExclusive(fn, isAllowed = () => true) {
      while (inFlight) {
        const gen = generation;
        try {
          await new Promise((resolve, reject) => {
            waiters.push({ resolve, reject, gen });
          });
        } catch (_err) {
          if (!isAllowed()) return undefined;
        }
        if (!isAllowed()) return undefined;
      }
      if (!isAllowed()) return undefined;
      inFlight = true;
      try {
        return await fn();
      } finally {
        inFlight = false;
        wakeNext();
      }
    },
  };
}

export function resetItemForPrepareRetry(item, options = {}) {
  if (!canRetryPrepare(item)) return item;
  if (!options.skipAbort) {
    abortQueueItemSession(item);
  }
  return {
    ...item,
    phase: QUEUE_PHASE.queued,
    prepareState: null,
    prepareError: null,
    prepareCode: null,
    uploadFile: null,
    runId: null,
    session: null,
    retryCount: item.retryCount + 1,
  };
}

/**
 * Run prepare for one queue item. Caller owns concurrency=1 scheduling via lock.
 */
export async function prepareQueueItem(item, options = {}) {
  const lock = options.lock;
  if (!lock) {
    throw new Error("prepareQueueItem requires a prepare lock instance");
  }

  return lock.runExclusive(async () => {
    const surface = surfaceForKind(item.kind);
    const session = createPrepareSession();
    const isCurrent = options.isCurrent || (() => true);

    options.onStartRun?.(item.localId, session.runId);

    const emitPatch = (patch) => {
      if (!isCurrent(item.localId, session.runId)) return;
      options.onPatch?.(item.localId, patch);
    };

    emitPatch({
      phase: QUEUE_PHASE.checking,
      prepareState: PREPARE_STATES.selected,
      runId: session.runId,
      session,
      prepareError: null,
    });

    const onStateChange = ({ state, code, detail }) => {
      if (!isCurrent(item.localId, session.runId)) return;
      const phase = preparePhaseFromState(state);
      emitPatch({
        prepareState: state,
        phase: state === PREPARE_STATES.failed ? QUEUE_PHASE.prepare_failed : phase,
        prepareError:
          state === PREPARE_STATES.failed
            ? detail || mapPrepareErrorMessage(code)
            : null,
        prepareCode: code || null,
      });
    };

    const result = await prepareImageForEditor(item.file, {
      surface,
      runId: session.runId,
      signal: session.signal,
      isCurrent: () => isCurrent(item.localId, session.runId),
      onStateChange,
      confirmServerPreview: async () => true,
      requestServerPreview: options.requestServerPreview,
      nativeDecode: options.nativeDecode,
      apiClient: options.apiClient,
      File: options.File,
    });

    if (!isCurrent(item.localId, session.runId)) {
      abortPrepareSessionOnce(session);
      return { cancelled: true };
    }

    if (
      result.kind === "passthrough"
      || result.kind === "native"
      || result.kind === "server"
    ) {
      const editorFile = result.file;
      let previewUrl = null;
      if (canLocalPreview(editorFile)) {
        previewUrl = URL.createObjectURL(editorFile);
      }
      emitPatch({
        uploadFile: editorFile,
        previewUrl,
        previewRevoked: false,
        phase: QUEUE_PHASE.ready,
        prepareState: result.state,
        prepareError: null,
        prepareCode: null,
        session: null,
      });
      return { ok: true, file: editorFile };
    }

    if (result.kind === "cancelled") {
      emitPatch({
        phase: QUEUE_PHASE.prepare_failed,
        prepareError: mapPrepareErrorMessage("cancelled_ui"),
        prepareCode: "cancelled_ui",
        session: null,
      });
      return { cancelled: true };
    }

    emitPatch({
      phase: QUEUE_PHASE.prepare_failed,
      prepareError: result.detail || mapPrepareErrorMessage(result.code),
      prepareCode: result.code || null,
      session: null,
    });
    return { failed: true, code: result.code };
  }, options.isAllowed || (() => true));
}

/**
 * Process HEIC queue items sequentially in selection order (concurrency = 1).
 */
export async function drainPropertyPrepareQueue(items, handlers = {}) {
  if (!handlers.lock) {
    throw new Error("drainPropertyPrepareQueue requires a prepare lock instance");
  }

  const pending = items.filter(
    (it) => it.heicLike && it.phase === QUEUE_PHASE.queued,
  );

  for (const item of pending) {
    if (handlers.shouldStop?.()) break;

    let live = item;
    if (typeof handlers.getItem === "function") {
      live = handlers.getItem(item.localId);
      // Removed while waiting: never fall back to a stale snapshot.
      if (!live) continue;
    }
    if (!live.heicLike || live.phase !== QUEUE_PHASE.queued) continue;

    await prepareQueueItem(live, {
      lock: handlers.lock,
      isCurrent: handlers.isCurrent,
      isAllowed: handlers.isAllowed,
      onStartRun: handlers.onStartRun,
      onPatch: handlers.onPatch,
      requestServerPreview: handlers.requestServerPreview,
      nativeDecode: handlers.nativeDecode,
      apiClient: handlers.apiClient,
      File: handlers.File,
    });
  }
}
